import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Grid,
  alpha,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  RadioButtonUnchecked as UncheckedIcon,
} from '@mui/icons-material';

// Measurement definitions with visual coordinates and descriptions
const MEASUREMENTS = {
  // Upper body - vertical
  front_waist_len: {
    label: 'Front Waist Length',
    description: 'From neck base to natural waist (front)',
    key: 'A',
    line: { x1: 200, y1: 80, x2: 200, y2: 240 },
    color: '#e91e63',
    group: 'upper_vertical',
  },
  back_waist_len: {
    label: 'Back Waist Length',
    description: 'From neck base to natural waist (back)',
    key: 'B',
    line: { x1: 220, y1: 80, x2: 220, y2: 240 },
    color: '#9c27b0',
    group: 'upper_vertical',
  },

  // Upper body - horizontal
  neck_circ: {
    label: 'Neck Circumference',
    description: 'Around the base of the neck',
    key: 'C',
    arc: { cx: 200, cy: 75, rx: 25, ry: 15 },
    color: '#3f51b5',
    group: 'upper_horizontal',
  },
  bust_circ: {
    label: 'Bust/Chest Circumference',
    description: 'Around the fullest part of the chest',
    key: 'D',
    arc: { cx: 200, cy: 150, rx: 65, ry: 30 },
    color: '#2196f3',
    group: 'upper_horizontal',
  },
  waist_circ: {
    label: 'Waist Circumference',
    description: 'Around the natural waistline',
    key: 'E',
    arc: { cx: 200, cy: 240, rx: 55, ry: 25 },
    color: '#00bcd4',
    group: 'upper_horizontal',
  },
  hip_circ: {
    label: 'Hip Circumference',
    description: 'Around the fullest part of the hips',
    key: 'F',
    arc: { cx: 200, cy: 300, rx: 70, ry: 30 },
    color: '#009688',
    group: 'lower_horizontal',
  },

  // Shoulders and arms
  shoulder_len: {
    label: 'Shoulder Length',
    description: 'From neck point to shoulder point',
    key: 'G',
    line: { x1: 200, y1: 80, x2: 135, y2: 105 },
    color: '#4caf50',
    group: 'shoulders',
  },
  armhole_depth: {
    label: 'Armhole Depth',
    description: 'From shoulder point to underarm',
    key: 'H',
    line: { x1: 135, y1: 105, x2: 135, y2: 165 },
    color: '#8bc34a',
    group: 'shoulders',
  },
  back_width: {
    label: 'Back Width',
    description: 'Across the back between armholes',
    key: 'I',
    line: { x1: 155, y1: 130, x2: 245, y2: 130 },
    color: '#cddc39',
    group: 'shoulders',
  },

  // Arms
  sleeve_length: {
    label: 'Sleeve Length',
    description: 'From shoulder point to wrist',
    key: 'J',
    line: { x1: 135, y1: 105, x2: 95, y2: 280 },
    color: '#ffeb3b',
    group: 'arms',
  },
  bicep_circ: {
    label: 'Bicep Circumference',
    description: 'Around the fullest part of upper arm',
    key: 'K',
    arc: { cx: 120, cy: 150, rx: 25, ry: 15, rotate: 70 },
    color: '#ffc107',
    group: 'arms',
  },
  wrist_circ: {
    label: 'Wrist Circumference',
    description: 'Around the wrist',
    key: 'L',
    arc: { cx: 95, cy: 280, rx: 12, ry: 8 },
    color: '#ff9800',
    group: 'arms',
  },

  // Bust details
  bust_point_height: {
    label: 'Bust Point Height',
    description: 'From shoulder to bust apex',
    key: 'M',
    line: { x1: 180, y1: 105, x2: 180, y2: 150 },
    color: '#ff5722',
    group: 'bust',
  },

  // Lower body - vertical
  inseam: {
    label: 'Inseam',
    description: 'From crotch to ankle (inner leg)',
    key: 'N',
    line: { x1: 180, y1: 320, x2: 180, y2: 530 },
    color: '#795548',
    group: 'lower_vertical',
  },
  outseam: {
    label: 'Outseam',
    description: 'From waist to ankle (outer leg)',
    key: 'O',
    line: { x1: 155, y1: 240, x2: 155, y2: 530 },
    color: '#9e9e9e',
    group: 'lower_vertical',
  },
  rise: {
    label: 'Rise',
    description: 'From waist to crotch',
    key: 'P',
    line: { x1: 200, y1: 240, x2: 200, y2: 320 },
    color: '#607d8b',
    group: 'lower_vertical',
  },
  skirt_length: {
    label: 'Skirt Length',
    description: 'From waist to desired hem',
    key: 'Q',
    line: { x1: 210, y1: 240, x2: 210, y2: 400 },
    color: '#e91e63',
    group: 'lower_vertical',
  },
  dress_length: {
    label: 'Dress Length',
    description: 'From shoulder to desired hem',
    key: 'R',
    line: { x1: 230, y1: 80, x2: 230, y2: 450 },
    color: '#9c27b0',
    group: 'lower_vertical',
  },

  // Lower body - horizontal
  high_hip_circ: {
    label: 'High Hip Circumference',
    description: 'Around the high hip (3" below waist)',
    key: 'S',
    arc: { cx: 200, cy: 270, rx: 63, ry: 28 },
    color: '#4caf50',
    group: 'lower_horizontal',
  },
  thigh_circ: {
    label: 'Thigh Circumference',
    description: 'Around the fullest part of thigh',
    key: 'T',
    arc: { cx: 180, cy: 340, rx: 30, ry: 18 },
    color: '#673ab7',
    group: 'lower_horizontal',
  },
  knee_circ: {
    label: 'Knee Circumference',
    description: 'Around the knee',
    key: 'U',
    arc: { cx: 180, cy: 430, rx: 22, ry: 14 },
    color: '#3f51b5',
    group: 'lower_horizontal',
  },
  ankle_circ: {
    label: 'Ankle Circumference',
    description: 'Around the ankle',
    key: 'V',
    arc: { cx: 180, cy: 530, rx: 15, ry: 10 },
    color: '#2196f3',
    group: 'lower_horizontal',
  },

  // Additional construction measurements
  waist_to_hem: {
    label: 'Waist to Hem',
    description: 'From waist to desired hem length',
    key: 'W',
    line: { x1: 245, y1: 240, x2: 245, y2: 380 },
    color: '#ff6f00',
    group: 'construction',
  },
  shoulder_slope_deg: {
    label: 'Shoulder Slope',
    description: 'Angle of shoulder slope (degrees)',
    key: 'X',
    line: { x1: 200, y1: 85, x2: 135, y2: 105 },
    color: '#f50057',
    group: 'construction',
  },
};

const MeasurementCroqui = ({ selectedMeasurements = [], onMeasurementToggle }) => {
  const [hoveredMeasurement, setHoveredMeasurement] = useState(null);

  const handleMeasurementClick = (measurementKey) => {
    if (onMeasurementToggle) {
      onMeasurementToggle(measurementKey);
    }
  };

  const isSelected = (measurementKey) => {
    return selectedMeasurements.includes(measurementKey);
  };

  const renderMeasurementLine = (measurement, measurementId) => {
    const isActive = isSelected(measurementId);
    const isHovered = hoveredMeasurement === measurementId;
    const { line, color, key: letterKey } = measurement;

    return (
      <g
        key={measurementId}
        onClick={() => handleMeasurementClick(measurementId)}
        onMouseEnter={() => setHoveredMeasurement(measurementId)}
        onMouseLeave={() => setHoveredMeasurement(null)}
        style={{ cursor: 'pointer' }}
      >
        {/* Measurement line */}
        <line
          x1={line.x1}
          y1={line.y1}
          x2={line.x2}
          y2={line.y2}
          stroke={isActive ? color : '#ccc'}
          strokeWidth={isActive || isHovered ? 3 : 2}
          strokeDasharray={isActive ? '0' : '5,5'}
          opacity={isActive || isHovered ? 1 : 0.5}
        />

        {/* End markers */}
        <circle cx={line.x1} cy={line.y1} r={4} fill={isActive ? color : '#ccc'} opacity={isActive || isHovered ? 1 : 0.5} />
        <circle cx={line.x2} cy={line.y2} r={4} fill={isActive ? color : '#ccc'} opacity={isActive || isHovered ? 1 : 0.5} />

        {/* Label key */}
        <text
          x={(line.x1 + line.x2) / 2 + 10}
          y={(line.y1 + line.y2) / 2}
          fill={isActive ? color : '#666'}
          fontSize="14"
          fontWeight={isActive || isHovered ? 'bold' : 'normal'}
        >
          {letterKey}
        </text>
      </g>
    );
  };

  const renderMeasurementArc = (measurement, measurementId) => {
    const isActive = isSelected(measurementId);
    const isHovered = hoveredMeasurement === measurementId;
    const { arc, color, key: letterKey } = measurement;

    return (
      <g
        key={measurementId}
        onClick={() => handleMeasurementClick(measurementId)}
        onMouseEnter={() => setHoveredMeasurement(measurementId)}
        onMouseLeave={() => setHoveredMeasurement(null)}
        style={{ cursor: 'pointer' }}
      >
        {/* Measurement arc/ellipse */}
        <ellipse
          cx={arc.cx}
          cy={arc.cy}
          rx={arc.rx}
          ry={arc.ry}
          transform={arc.rotate ? `rotate(${arc.rotate} ${arc.cx} ${arc.cy})` : ''}
          stroke={isActive ? color : '#ccc'}
          strokeWidth={isActive || isHovered ? 3 : 2}
          strokeDasharray={isActive ? '0' : '5,5'}
          fill="none"
          opacity={isActive || isHovered ? 1 : 0.5}
        />

        {/* Label key */}
        <text
          x={arc.cx + arc.rx + 10}
          y={arc.cy}
          fill={isActive ? color : '#666'}
          fontSize="14"
          fontWeight={isActive || isHovered ? 'bold' : 'normal'}
        >
          {letterKey}
        </text>
      </g>
    );
  };

  // Group measurements by category for the legend
  const measurementsByGroup = Object.entries(MEASUREMENTS).reduce((acc, [measurementId, measurementData]) => {
    const groupName = measurementData.group;
    if (!acc[groupName]) {
      acc[groupName] = [];
    }
    acc[groupName].push({ measurementId, ...measurementData });
    return acc;
  }, {});

  const groupLabels = {
    upper_vertical: 'Upper Body - Vertical',
    upper_horizontal: 'Upper Body - Horizontal',
    shoulders: 'Shoulders & Back',
    arms: 'Arms',
    bust: 'Bust Details',
    lower_vertical: 'Lower Body - Vertical',
    lower_horizontal: 'Lower Body - Horizontal',
    construction: 'Construction & Fit',
  };

  return (
    <Box>
      <Grid container spacing={3}>
        {/* Croqui Diagram */}
        <Grid item xs={12} md={7}>
          <Paper elevation={2} sx={{ p: 3, bgcolor: 'grey.50' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Select Measurements on Croqui
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Click on measurement lines to select/deselect them for your pattern
            </Typography>

            <Box
              sx={{
                width: '100%',
                height: 600,
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                bgcolor: 'white',
                borderRadius: 1,
                border: '1px solid',
                borderColor: 'divider',
              }}
            >
              <svg width="400" height="600" viewBox="0 0 400 600">
                {/* Basic croqui body outline */}
                <g id="body" stroke="#333" strokeWidth="2" fill="none">
                  {/* Head */}
                  <circle cx="200" cy="40" r="25" fill="#f5f5f5" />

                  {/* Neck */}
                  <line x1="200" y1="65" x2="200" y2="80" />

                  {/* Shoulders */}
                  <line x1="200" y1="80" x2="135" y2="105" />
                  <line x1="200" y1="80" x2="265" y2="105" />

                  {/* Arms */}
                  <path d="M 135 105 Q 120 180, 95 280" />
                  <path d="M 265 105 Q 280 180, 305 280" />

                  {/* Torso - front silhouette */}
                  <path d="M 135 105 L 135 165 Q 140 240, 145 240 L 145 310 L 175 530" fill="#f9f9f9" />
                  <path d="M 265 105 L 265 165 Q 260 240, 255 240 L 255 310 L 225 530" fill="#f9f9f9" />

                  {/* Center front line */}
                  <line x1="200" y1="80" x2="200" y2="530" stroke="#ccc" strokeDasharray="3,3" />
                </g>

                {/* Render all measurements */}
                {Object.entries(MEASUREMENTS).map(([measurementId, measurement]) => {
                  if (measurement.line) {
                    return renderMeasurementLine(measurement, measurementId);
                  } else if (measurement.arc) {
                    return renderMeasurementArc(measurement, measurementId);
                  }
                  return null;
                })}

                {/* Hover tooltip area */}
                {hoveredMeasurement && MEASUREMENTS[hoveredMeasurement] && (
                  <g>
                    <rect
                      x="10"
                      y="560"
                      width="380"
                      height="35"
                      fill="white"
                      stroke="#333"
                      strokeWidth="1"
                      rx="4"
                    />
                    <text x="20" y="578" fontSize="12" fontWeight="bold">
                      {MEASUREMENTS[hoveredMeasurement].key}: {MEASUREMENTS[hoveredMeasurement].label}
                    </text>
                    <text x="20" y="590" fontSize="10" fill="#666">
                      {MEASUREMENTS[hoveredMeasurement].description}
                    </text>
                  </g>
                )}
              </svg>
            </Box>
          </Paper>
        </Grid>

        {/* Measurement Legend/Key */}
        <Grid item xs={12} md={5}>
          <Paper elevation={2} sx={{ p: 3, height: '100%', maxHeight: 800, overflow: 'auto' }}>
            <Typography variant="h6" fontWeight={600} gutterBottom>
              Measurement Key
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              {selectedMeasurements.length} measurement{selectedMeasurements.length !== 1 ? 's' : ''} selected
            </Typography>

            {Object.entries(groupLabels).map(([groupKey, groupLabel]) => {
              const groupMeasurements = measurementsByGroup[groupKey] || [];
              if (groupMeasurements.length === 0) return null;

              return (
                <Box key={groupKey} sx={{ mb: 3 }}>
                  <Typography variant="subtitle2" fontWeight={600} sx={{ mb: 1, color: 'primary.main' }}>
                    {groupLabel}
                  </Typography>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0 }}>
                    {groupMeasurements.map((measurement) => {
                      const { measurementId, key: letterKey, label, description, color } = measurement;
                      const selected = isSelected(measurementId);
                      return (
                        <Box
                          key={measurementId}
                          sx={{
                            cursor: 'pointer',
                            py: 1.5,
                            px: 1,
                            borderBottom: '1px solid',
                            borderColor: 'divider',
                            bgcolor: selected ? alpha(color, 0.04) : 'transparent',
                            transition: 'background-color 0.2s',
                            '&:hover': {
                              bgcolor: alpha(color, 0.08),
                            },
                          }}
                          onClick={() => handleMeasurementClick(measurementId)}
                          onMouseEnter={() => setHoveredMeasurement(measurementId)}
                          onMouseLeave={() => setHoveredMeasurement(null)}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            {selected ? (
                              <CheckIcon sx={{ color: color, fontSize: 20 }} />
                            ) : (
                              <UncheckedIcon sx={{ color: 'text.disabled', fontSize: 20 }} />
                            )}
                            <Box
                              sx={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                bgcolor: color,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'center',
                                color: 'white',
                                fontSize: '0.75rem',
                                fontWeight: 'bold',
                              }}
                            >
                              {letterKey}
                            </Box>
                            <Box sx={{ flexGrow: 1 }}>
                              <Typography variant="body2" fontWeight={selected ? 600 : 400}>
                                {label}
                              </Typography>
                              <Typography variant="caption" color="text.secondary">
                                {description}
                              </Typography>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Box>
                </Box>
              );
            })}
          </Paper>
        </Grid>

        {/* Selected Measurements Summary */}
        {selectedMeasurements.length > 0 && (
          <Grid item xs={12}>
            <Paper elevation={2} sx={{ p: 2, bgcolor: 'primary.50', borderLeft: 4, borderColor: 'primary.main' }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Selected Measurements ({selectedMeasurements.length})
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, mt: 1 }}>
                {selectedMeasurements.map((measurementId) => {
                  const measurement = MEASUREMENTS[measurementId];
                  if (!measurement) return null;
                  return (
                    <Chip
                      key={measurementId}
                      label={`${measurement.key}: ${measurement.label}`}
                      onDelete={() => handleMeasurementClick(measurementId)}
                      sx={{
                        bgcolor: measurement.color,
                        color: 'white',
                        fontWeight: 600,
                        '& .MuiChip-deleteIcon': {
                          color: 'white',
                        },
                      }}
                    />
                  );
                })}
              </Box>
            </Paper>
          </Grid>
        )}
      </Grid>
    </Box>
  );
};

export default MeasurementCroqui;
